import createCaseFormStyle from "./createCaseFormStyle";
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import useForm, { UseFormProps } from "../../hooks/useForm";
import ValidatorSelect, { ValidatorSelectProps } from "../Validator/ValidatorSelect";
import MenuItem from "@mui/material/MenuItem";
import { convertToFloat, formValue } from "../../services/formServiceFunctions";
import { Checkbox, FormControlLabel, InputLabel, SelectChangeEvent, Stack, TextField } from "@mui/material";
import ValidatorTextField from "../Validator/ValidatorTextField";
import ButtonLoading from "../Button/ButtonLoading";
import _ from "lodash";
import { getCaseLink, getProductData } from "../../services/Product/ProductService/productService";

import { useSnackbar } from "notistack";

import {
  atLeastOneFieldRequiredDefault,
  caseReferenceNumberIsUnique,
  emailDefault,
  isPhoneNumberWithoutSpecialCharactersDefault,
  matchRegexp,
  requiredFieldDefault,
} from "../../services/validationRules";
import { translate } from "../../services/Translations/translatorService";
import ApiClient from "../../services/ApiClient";
import PageHeadline from "../PageHeadline/PageHeadline";
import ContentBox from "../ContentBox/ContentBox";
import { userHasRole } from "../../services/backofficeUserService";
import { Roles } from "../../types/BackofficeUser";
import { apiGet } from "../../services/Api/apiCall";
import { useMutation } from "@tanstack/react-query";
import { createResource } from "../../services/ReactQuery/reactQueryService";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import ValidatorNumberField from "../Validator/ValidatorNumberField";
import InfoBox from "../InfoBox/InfoBox";
import { ValidatorType } from "../../types/ValidatorType";
import usePartnerRegistry from "../../hooks/usePartnerRegistry";
import { RuntimePartnerReference } from "../../types/PartnerReference";
import { getPartnerIri } from "../../services/partnerReferenceService";

const SUPPORTED_CREATE_CASE_PARTNERS = ["advocard", "arag", "auxilia", "nrv", "wbs", "hukCoburg", "deurag", "lvm"];
const isSelectableCreateCasePartner = (
  partner: RuntimePartnerReference,
): partner is RuntimePartnerReference & { name: string } => {
  return typeof partner.name === "string" && partner.name !== "";
};

const CreateCase = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const [customerStatus, setCustomerStatus] = useState("notChecked");
  const [hasNoEmail, setHasNoEmail] = useState(false);
  const currentUser = useCurrentUser();
  const isCallcenterUser = userHasRole(currentUser, Roles.callcenter);
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const { activePartners, partnersBySlug, isLoading: isPartnerRegistryLoading } = usePartnerRegistry();

  const initialValues = {
    meta: {
      legalField: "",
      subLegalField: "",
    },
    customer: {
      email: "",
      telephone: "",
      gclid: "backoffice",
      person: {
        gender: "",
        givenName: "",
        familyName: "",
      },
      prelead: {
        initialProduct: "",
        landingPage: "Legalbird.io",
        partner: "",
      },
    },
    caseInsurance: {
      caseReferenceNumber: "",
      insurancePolicyNumber: "",
      deductible: "",
      subjectMatter: "",
    },
    note: "",
  };

  const fetchCustomer = async (email: string) => {
    const customerCollection = await apiGet("customers", null, { email: email });
    if (customerCollection["hydra:totalItems"] === 0) {
      return null;
    }
    return _.head(customerCollection["hydra:member"]);
  };

  const onSubmit: UseFormProps["onSubmit"] = async ({ values }) => {
    const productName = values.meta.subLegalField;
    const acquisitionPartnerIri = getPartnerIri(partnersBySlug[values.customer.prelead.partner]);

    if (!acquisitionPartnerIri) {
      alert("Der Kooperationspartner konnte nicht als Partner-Referenz aufgelöst werden.");
      return;
    }

    //-- manipulate initialProduct
    let customerData = _.merge({}, values.customer);
    customerData.person.gender = customerData.person.gender || null;
    customerData.prelead.initialProduct = productName;

    let customer;

    switch (true) {
      case hasNoEmail:
        customerData.email = `advocard.error+${values.caseInsurance.insurancePolicyNumber}@gmail.com`;
        const backofficeCases = await getBackofficeCasesForEmail(customerData.email);
        if (backofficeCases.length > 0) {
          customer = await fetchCustomer(customerData.email);
          break;
        }
        customer = await createMutation.mutateAsync({ uri: "customers", data: customerData });
        break;
      default:
        customer =
          customerStatus === "newCustomer"
            ? await createMutation.mutateAsync({ uri: "customers", data: customerData })
            : await fetchCustomer(customerData.email);
        break;
    }

    if (!customer) {
      alert("Es ist ein technischer Fehler beim Auffinden des bestehenden Kunden aufgetreten!");
      return;
    }

    //-- add product
    const productUri = getProductData(productName, "apiUri");
    let productData: Record<string, any> = {
      customer: customer["@id"],
      acquisitionPartner: acquisitionPartnerIri,
      createdBy: isCallcenterUser ? "backoffice_callcenter" : "backoffice",
      caseInsurance: {
        caseReferenceNumber:
          values.caseInsurance.caseReferenceNumber === "" ? null : values.caseInsurance.caseReferenceNumber,
        insurancePolicyNumber: values.caseInsurance.insurancePolicyNumber,
        deductible: values.caseInsurance.deductible ? convertToFloat(values.caseInsurance.deductible) : null,
        subjectMatter: values.caseInsurance.subjectMatter,
      },
    };

    const productEntity = await createMutation.mutateAsync({ uri: productUri, data: productData });

    if (!_.isEmpty(values.note)) {
      await createMutation.mutateAsync({
        uri: "notes",
        data: {
          content: values.note,
          pinned: true,
          case: productEntity.backofficeCase["@id"],
        },
      });
    }

    enqueueSnackbar("Mandantschaft erfolgreich angelegt", {
      variant: "custom",
      buttonType: "link",
      buttonLink: getCaseLink(productEntity, isCallcenterUser ? "/callcenter" : ""),
      buttonText: "Zum Fall",
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
      autoHideDuration: 10000,
    });
    clearForm();
  };

  const getSubLegalFields = (partner: string, legalField: string) => {
    switch (legalField) {
      case "familyLaw":
        return [
          ...(["nrv", "deurag", "lvm", "auxilia"].includes(partner)
            ? []
            : [
                <MenuItem key={"divorce"} value={"divorce"}>
                  Trennung/Scheidung
                </MenuItem>,
              ]),
          <MenuItem key={"alimony"} value={"alimony"}>
            Unterhalt
          </MenuItem>,
          <MenuItem key={"death"} value={"death"}>
            Erbe/Todesfall
          </MenuItem>,
          <MenuItem key={"familyLawCase"} value={"familyLawCase"}>
            Anderes Thema
          </MenuItem>,
        ];
      case "trafficLaw":
        return [
          <MenuItem key={"traffic"} value={"traffic"}>
            Ordnungswidrigkeit
          </MenuItem>,
          ...(["nrv"].includes(partner)
            ? [
                <MenuItem key={"trafficAccident"} value={"trafficAccident"}>
                  Verkehrsunfall
                </MenuItem>,
              ]
            : []),
        ];
      case "employmentLaw":
        return [
          ...(["advocard", "nrv", "hukCoburg"].includes(partner)
            ? [
                <MenuItem key={"employmentContract"} value={"employmentContract"}>
                  {getProductData("employmentContract", "label")}
                </MenuItem>,
              ]
            : []),
          <MenuItem key="settlement" value="settlement">
            {getProductData("settlement", "label")}
          </MenuItem>,
        ];
      case "contractLaw":
        if (partner === "hukCoburg") {
          return [
            <MenuItem key={"travelContract"} value={"travelContract"}>
              {getProductData("travelContract", "label")}
            </MenuItem>,
            <MenuItem key={"otherContract"} value={"otherContract"}>
              {getProductData("otherContract", "label")}
            </MenuItem>,
          ];
        }

        return [
          <MenuItem key={"purchaseContract"} value={"purchaseContract"}>
            {getProductData("purchaseContract", "label")}
          </MenuItem>,
          <MenuItem key={"vehicleContract"} value={"vehicleContract"}>
            {getProductData("vehicleContract", "label")}
          </MenuItem>,
          <MenuItem key={"serviceContract"} value={"serviceContract"}>
            {getProductData("serviceContract", "label")}
          </MenuItem>,
          <MenuItem key={"membershipContract"} value={"membershipContract"}>
            {getProductData("membershipContract", "label")}
          </MenuItem>,
          <MenuItem key={"subscriptionContract"} value={"subscriptionContract"}>
            {getProductData("subscriptionContract", "label")}
          </MenuItem>,
          <MenuItem key={"providerContract"} value={"providerContract"}>
            {getProductData("providerContract", "label")}
          </MenuItem>,
          <MenuItem key={"dataLeakContract"} value={"dataLeakContract"}>
            {getProductData("dataLeakContract", "label")}
          </MenuItem>,
          <MenuItem key={"travelContract"} value={"travelContract"}>
            {getProductData("travelContract", "label")}
          </MenuItem>,
          <MenuItem key={"eventContract"} value={"eventContract"}>
            {getProductData("eventContract", "label")}
          </MenuItem>,
          <MenuItem key={"otherContract"} value={"otherContract"}>
            {getProductData("otherContract", "label")}
          </MenuItem>,
        ];
      case "tenancyLaw":
        return [
          <MenuItem key={"rentalContract"} value={"rentalContract"}>
            {getProductData("rentalContract", "label")}
          </MenuItem>,
        ];
      case "civilLaw":
        return [
          <MenuItem key={"generalCivilLaw"} value={"generalCivilLaw"}>
            {getProductData("generalCivilLaw", "label")}
          </MenuItem>,
        ];
      case "administrativeLaw":
        return [
          <MenuItem key={"civilServiceLaw"} value={"civilServiceLaw"}>
            Beamtenrecht
          </MenuItem>,
        ];
      default:
        return [];
    }
  };

  const hasLegalField = (
    legalField: string,
    partner: "advocard" | "arag" | "auxilia" | "nrv" | "wbs" | "hukCoburg" | "deurag" | "lvm",
  ) => {
    const legalFields = {
      advocard: ["familyLaw", "trafficLaw", "tenancyLaw", "contractLaw", "civilLaw", "employmentLaw"],
      arag: ["familyLaw", "trafficLaw", "tenancyLaw"],
      auxilia: ["familyLaw"],
      deurag: ["familyLaw"],
      lvm: ["familyLaw", "tenancyLaw", "contractLaw", "civilLaw"],
      nrv: ["familyLaw", "trafficLaw", "tenancyLaw", "contractLaw", "civilLaw", "employmentLaw"],
      wbs: ["trafficLaw", "employmentLaw", "tenancyLaw"],
      hukCoburg: ["administrativeLaw", "contractLaw", "civilLaw", "employmentLaw", "tenancyLaw"],
    };

    return _.includes(legalFields[partner], legalField);
  };

  const { values, handleChange, handleSubmit, handleBlur, clearForm, registerValidators, errors, isLoading } = useForm({
    initialValues,
    onSubmit,
    identifier: "registerCustomer",
  });

  const updateInsuranceNumberValidators = (partner: string) => {
    if (["hukCoburg"].includes(partner)) {
      registerValidators("caseInsurance.insurancePolicyNumber", [...requiredFieldDefault]);
      return;
    }

    if (!["advocard"].includes(partner)) {
      registerValidators("caseInsurance.insurancePolicyNumber", []);
      return;
    }

    let newValidators: ValidatorType[] = hasNoEmail
      ? [...requiredFieldDefault]
      : atLeastOneFieldRequiredDefault("caseInsurance.caseReferenceNumber");

    registerValidators("caseInsurance.insurancePolicyNumber", newValidators);
  };

  const handlePartnerChange: ValidatorSelectProps["onChange"] = (e) => {
    handleChange({ target: { type: "text", name: "meta.legalField", value: "" } });
    handleChange({ target: { type: "text", name: "meta.subLegalField", value: "" } });

    // reset the stage of hasNoEmail otherwise other partners
    // could not disable the checkbox after selecting advocard once
    if (e.target.value !== "advocard") {
      setHasNoEmail(false);
    }

    switch (e.target.value) {
      case "advocard":
        registerValidators("caseInsurance.caseReferenceNumber", [
          ...atLeastOneFieldRequiredDefault("caseInsurance.insurancePolicyNumber"),
          {
            validator: matchRegexp,
            params: ["^\\d{2}-?(33-?0001-?|\\d)\\d{5}-?\\d$"],
            message: "Bitte eine valide Advocard Schadennummer angeben",
          },
          {
            validator: caseReferenceNumberIsUnique,
            params: [""],
            message: "Diese Schadennummer existiert bereits in einem anderen Rechtsfall.",
          },
        ]);
        break;
      case "arag":
      case "auxilia":
      case "deurag":
      case "lvm":
      case "nrv":
        registerValidators("caseInsurance.caseReferenceNumber", [
          ...atLeastOneFieldRequiredDefault("caseInsurance.insurancePolicyNumber"),
          ...requiredFieldDefault,
          {
            validator: caseReferenceNumberIsUnique,
            params: [""],
            message: "Diese Schadennummer existiert bereits in einem anderen Rechtsfall.",
          },
        ]);
        break;
      case "hukCoburg":
        break;
      case "wbs":
        registerValidators("caseInsurance.caseReferenceNumber", [
          {
            validator: caseReferenceNumberIsUnique,
            params: [""],
            message: "Diese Schadennummer existiert bereits in einem anderen Rechtsfall.",
          },
        ]);
        break;
      default:
        return;
    }
    updateInsuranceNumberValidators(e.target.value);
    handleChange(e);
  };

  const getBackofficeCasesForEmail = async (email: string) => {
    const backofficeCasesResponse = await ApiClient.get("backoffice_cases?customerEmail=" + encodeURIComponent(email));
    return backofficeCasesResponse["hydra:member"];
  };

  const handleDuplicateBlur: ValidatorSelectProps["onBlur"] = async (e) => {
    handleBlur(e);
    if (values.customer.email === "") {
      return;
    }
    setCustomerStatus("notChecked");
    setIsLoadingInternal(true);
    const customer = await fetchCustomer(values.customer.email.toLowerCase());
    setCustomerStatus(!!customer ? "existingCustomer" : "newCustomer");
    setIsLoadingInternal(false);
  };

  useEffect(() => {
    updateInsuranceNumberValidators(formValue(values, "customer.prelead.partner"));
    if (hasNoEmail) {
      registerValidators("customer.email", []);
      handleChange({ target: { name: "customer.email", value: "" } });
      handleBlur({ target: { name: "customer.email", value: "" } });
      return;
    }
    registerValidators("customer.email", [...emailDefault, ...requiredFieldDefault]);
  }, [hasNoEmail]);

  const handleLegalFieldChange = (event: SelectChangeEvent) => {
    const value = event.target.value;

    const subLegalFields = getSubLegalFields(formValue(values, "customer.prelead.partner"), value);

    if (subLegalFields.length === 1) {
      handleChange({ target: { type: "text", name: "meta.subLegalField", value: subLegalFields[0].props.value } });
    } else {
      handleChange({ target: { type: "text", name: "meta.subLegalField", value: "" } });
    }

    handleChange(event);
  };

  const activePartnerOptions = activePartners.filter(
    (partner): partner is RuntimePartnerReference & { name: string } => {
      if (!isSelectableCreateCasePartner(partner) || !SUPPORTED_CREATE_CASE_PARTNERS.includes(partner.name)) {
        return false;
      }

      return !(partner.name === "wbs" && isCallcenterUser);
    },
  );

  if (isPartnerRegistryLoading) {
    return null;
  }

  return (
    <>
      <PageHeadline main="Mandantschaft/Fall anlegen" />
      <ContentBox>
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={4}
            sx={{
              alignItems: "flex-start",
            }}
          >
            <Grid
              size={{
                lg: 4,
                md: 6,
                xs: 12,
              }}
            >
              <ValidatorSelect
                label={"Kooperationspartner"}
                name={"customer.prelead.partner"}
                value={formValue(values, "customer.prelead.partner")}
                onChange={handlePartnerChange}
                onBlur={handleBlur}
                registerValidators={registerValidators}
                validators={requiredFieldDefault}
                error={!!errors["customer.prelead.partner"]}
                helperText={errors["customer.prelead.partner"]}
              >
                {activePartnerOptions.map((partner) => (
                  <MenuItem key={partner["@id"]} value={partner.name}>
                    {partner.brandName || partner.name}
                  </MenuItem>
                ))}
              </ValidatorSelect>
            </Grid>
            <Grid
              size={{
                lg: 4,
                md: 6,
                xs: 12,
              }}
            >
              <ValidatorSelect
                label={"Rechtsbereich"}
                name={"meta.legalField"}
                value={formValue(values, "meta.legalField")}
                onChange={handleLegalFieldChange}
                onBlur={handleBlur}
                registerValidators={registerValidators}
                validators={requiredFieldDefault}
                error={!!errors["meta.legalField"]}
                helperText={errors["meta.legalField"]}
              >
                {hasLegalField("familyLaw", values.customer.prelead.partner) && (
                  <MenuItem value={"familyLaw"}>Familienrecht</MenuItem>
                )}
                {hasLegalField("contractLaw", values.customer.prelead.partner) && (
                  <MenuItem value={"contractLaw"}>Vertragsrecht</MenuItem>
                )}
                {hasLegalField("trafficLaw", values.customer.prelead.partner) && (
                  <MenuItem value={"trafficLaw"}>Verkehrsrecht</MenuItem>
                )}
                {hasLegalField("employmentLaw", values.customer.prelead.partner) && (
                  <MenuItem value={"employmentLaw"}>Arbeitsrecht</MenuItem>
                )}
                {hasLegalField("tenancyLaw", values.customer.prelead.partner) && (
                  <MenuItem value={"tenancyLaw"}>Mietrecht</MenuItem>
                )}
                {hasLegalField("administrativeLaw", values.customer.prelead.partner) && (
                  <MenuItem value={"administrativeLaw"}>Verwaltungsrecht</MenuItem>
                )}
                {hasLegalField("civilLaw", values.customer.prelead.partner) && (
                  <MenuItem value={"civilLaw"}>Zivilrecht</MenuItem>
                )}
              </ValidatorSelect>
            </Grid>
            <Grid
              size={{
                lg: 4,
                md: 6,
                xs: 12,
              }}
            >
              <ValidatorSelect
                label={"Thema"}
                name={"meta.subLegalField"}
                value={formValue(values, "meta.subLegalField")}
                onChange={handleChange}
                onBlur={handleDuplicateBlur}
                registerValidators={registerValidators}
                validators={requiredFieldDefault}
                error={!!errors["meta.subLegalField"]}
                helperText={errors["meta.subLegalField"]}
              >
                {getSubLegalFields(values.customer.prelead.partner, values.meta.legalField)}
              </ValidatorSelect>
            </Grid>
            <Grid
              size={{
                lg: 4,
                md: 6,
                xs: 12,
              }}
            >
              <ValidatorSelect
                displayEmpty
                label={"Anrede"}
                name={"customer.person.gender"}
                value={formValue(values, "customer.person.gender")}
                onChange={handleChange}
                onBlur={handleBlur}
                registerValidators={registerValidators}
                error={!!errors["customer.person.gender"]}
                helperText={errors["customer.person.gender"]}
              >
                <MenuItem value={""}>Keine Anrede</MenuItem>
                <MenuItem value={"female"}>Frau</MenuItem>
                <MenuItem value={"male"}>Herr</MenuItem>
              </ValidatorSelect>
            </Grid>
            <Grid
              size={{
                lg: 4,
                md: 6,
                xs: 12,
              }}
            >
              <ValidatorTextField
                fullWidth={true}
                name="customer.person.givenName"
                label="Vorname"
                value={formValue(values, "customer.person.givenName")}
                onChange={handleChange}
                onBlur={handleBlur}
                registerValidators={registerValidators}
                validators={requiredFieldDefault}
                error={!!errors["customer.person.givenName"]}
                helperText={errors["customer.person.givenName"]}
                isHighlighted={undefined}
                initialDependentValidationFields={undefined}
              />
            </Grid>
            <Grid
              size={{
                lg: 4,
                md: 6,
                xs: 12,
              }}
            >
              <ValidatorTextField
                fullWidth={true}
                name="customer.person.familyName"
                label="Nachname"
                value={formValue(values, "customer.person.familyName")}
                onChange={handleChange}
                onBlur={handleBlur}
                registerValidators={registerValidators}
                validators={requiredFieldDefault}
                error={!!errors["customer.person.familyName"]}
                helperText={errors["customer.person.familyName"]}
                isHighlighted={undefined}
                initialDependentValidationFields={undefined}
              />
            </Grid>
            <Grid
              size={{
                lg: 4,
                md: 6,
                xs: 12,
              }}
            >
              <ValidatorTextField
                fullWidth={true}
                name="customer.email"
                label="E-Mail-Adresse"
                value={formValue(values, "customer.email")}
                onChange={handleChange}
                onBlur={handleDuplicateBlur}
                disabled={values.meta.subLegalField === "" || hasNoEmail}
                registerValidators={registerValidators}
                validators={[...emailDefault, ...requiredFieldDefault]}
                error={!!errors["customer.email"]}
                helperText={errors["customer.email"]}
                isHighlighted={undefined}
                initialDependentValidationFields={undefined}
                slotProps={{
                  htmlInput: {
                    autoComplete: "off",
                  },
                }}
              />
            </Grid>
            {values.customer.prelead.partner === "advocard" && (
              <Grid
                size={{
                  lg: 4,
                  md: 6,
                  xs: 12,
                }}
              >
                <Stack
                  sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80px",
                  }}
                >
                  <FormControlLabel
                    disabled={_.includes(["trafficLaw"], values.meta.legalField)}
                    control={<Checkbox checked={hasNoEmail} onChange={(e) => setHasNoEmail(e.target.checked)} />}
                    label={"Erstberatung ohne Email"}
                  />
                </Stack>
              </Grid>
            )}
            <Grid
              size={{
                lg: 4,
                md: 6,
                xs: 12,
              }}
            >
              <ValidatorTextField
                fullWidth={true}
                name="customer.telephone"
                label="Telefonnummer"
                value={formValue(values, "customer.telephone")}
                onChange={handleChange}
                onBlur={handleBlur}
                registerValidators={registerValidators}
                validators={[...requiredFieldDefault, ...isPhoneNumberWithoutSpecialCharactersDefault]}
                error={!!errors["customer.telephone"]}
                helperText={errors["customer.telephone"]}
                isHighlighted={undefined}
                initialDependentValidationFields={undefined}
              />
            </Grid>
            {!["wbs", "hukCoburg"].includes(values.customer.prelead.partner) && (
              <Grid
                size={{
                  lg: 4,
                  md: 6,
                  xs: 12,
                }}
              >
                <ValidatorTextField
                  fullWidth={true}
                  name="caseInsurance.caseReferenceNumber"
                  label={translate("caseInsurance.caseReferenceNumber.label")}
                  value={formValue(values, "caseInsurance.caseReferenceNumber")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  registerValidators={registerValidators}
                  validators={[
                    {
                      validator: caseReferenceNumberIsUnique,
                      params: [""],
                      message: "Diese Schadennummer existiert bereits in einem anderen Rechtsfall.",
                    },
                    ...atLeastOneFieldRequiredDefault("caseInsurance.insurancePolicyNumber"),
                  ]}
                  error={!!errors["caseInsurance.caseReferenceNumber"]}
                  helperText={errors["caseInsurance.caseReferenceNumber"]}
                  isHighlighted={undefined}
                  initialDependentValidationFields={undefined}
                />
              </Grid>
            )}
            {["advocard", "hukCoburg"].includes(values.customer.prelead.partner) && (
              <Grid
                size={{
                  lg: 4,
                  md: 6,
                  xs: 12,
                }}
              >
                <ValidatorTextField
                  fullWidth={true}
                  name="caseInsurance.insurancePolicyNumber"
                  label={translate("caseInsurance.insurancePolicyNumber.label")}
                  value={formValue(values, "caseInsurance.insurancePolicyNumber")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors["caseInsurance.insurancePolicyNumber"]}
                  helperText={errors["caseInsurance.insurancePolicyNumber"]}
                  registerValidators={undefined}
                  validators={undefined}
                  isHighlighted={undefined}
                  initialDependentValidationFields={undefined}
                />
              </Grid>
            )}
            {values.customer.prelead.partner === "hukCoburg" && (
              <Grid
                size={{
                  lg: 4,
                  md: 6,
                  xs: 12,
                }}
              >
                <ValidatorTextField
                  fullWidth={true}
                  name="caseInsurance.subjectMatter"
                  label={translate("caseInsurance.subjectMatter.label")}
                  value={formValue(values, "caseInsurance.subjectMatter")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  registerValidators={registerValidators}
                  validators={requiredFieldDefault}
                  error={!!errors["caseInsurance.subjectMatter"]}
                  helperText={errors["caseInsurance.subjectMatter"]}
                />
              </Grid>
            )}
            {values.meta.legalField === "trafficLaw" && (
              <Grid
                size={{
                  lg: 4,
                  md: 6,
                  xs: 12,
                }}
              >
                <ValidatorNumberField
                  fullWidth={true}
                  name="caseInsurance.deductible"
                  label="Selbstbehalt"
                  value={formValue(values, "caseInsurance.deductible")}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  registerValidators={registerValidators}
                  error={!!errors["caseInsurance.deductible"]}
                  helperText={errors["caseInsurance.deductible"]}
                  numberType={"float"}
                  fieldType={"money"}
                />
              </Grid>
            )}
            {customerStatus === "existingCustomer" && (
              <Grid size={12}>
                <InfoBox>
                  Wichtiger Hinweis: Zu dieser E-Mail liegt bereits ein Kundenkonto vor. Es wird ein neuer Fall
                  angelegt.
                </InfoBox>
              </Grid>
            )}
            {!userHasRole(currentUser, Roles.callcenter) && (
              <Grid size={12}>
                <InputLabel>Notiz</InputLabel>
                <TextField
                  multiline
                  rows={8}
                  sx={createCaseFormStyle.textField}
                  name={"note"}
                  value={formValue(values, "note")}
                  onChange={handleChange}
                />
              </Grid>
            )}
            <Grid size={12}>
              <Grid
                container
                sx={{
                  justifyContent: "center",
                }}
              >
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                  }}
                >
                  <Stack direction="column" spacing={2}>
                    <ButtonLoading
                      fullWidth
                      variant="contained"
                      type="submit"
                      disabled={customerStatus === "notChecked" && !hasNoEmail}
                      isLoading={isLoading || isLoadingInternal}
                    >
                      Jetzt anlegen
                    </ButtonLoading>
                    <ButtonLoading fullWidth variant="text" onClick={() => clearForm()}>
                      Abbrechen
                    </ButtonLoading>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </ContentBox>
    </>
  );
};

export default CreateCase;
